import type {
  CustomerCreditSummaryReadback,
  ReceivableBalanceReadback,
} from "./receivable-settlement-client";

export type ReceivableSettlementAllItem = {
  receivable_id: string;
  remaining_amount: number;
};

export type ReceivableSettlementAllRequest = {
  deviceSecret: string;
  sessionToken: string;
  customerId: string;
  receivables: ReceivableSettlementAllItem[];
  expectedTotal: number;
  paymentMethodCode: string;
  receivingDestinationId: string | null;
  tenderedAmount: number | null;
  localChangeAmount: number | null;
  verificationSource: "CASH_TENDERED" | "STAFF_CONFIRMED";
  verificationNote?: string | null;
  requestKey: string;
  clientContext?: Record<string, unknown>;
};

export type ReceivableSettlementAllResult = {
  ok: boolean;
  contract: string;
  result_status: "COMMITTED" | "IDEMPOTENT_REPLAY" | string;
  idempotent_replay: boolean;
  customer_id: string;
  currency_code?: string;
  collection_total: number;
  receivable_count: number;
  payments: Array<Record<string, unknown>>;
  allocations: Array<Record<string, unknown>>;
  receivable_balances: ReceivableBalanceReadback[];
  customer_credit_summary: CustomerCreditSummaryReadback;
};

type GatewayEnvelope<T> = {
  ok: boolean;
  data?: T;
  error?: string;
  reason_code?: string;
};

export class PosReceivableSettlementAllGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: { baseUrl: string; timeoutMs?: number }) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 20_000;
  }

  async settleAll(input: ReceivableSettlementAllRequest): Promise<{
    ok: boolean;
    data: ReceivableSettlementAllResult | null;
    error: string | null;
    reasonCode: string | null;
  }> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/receivable-settlement-all`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_secret: input.deviceSecret,
          session_token: input.sessionToken,
          customer_id: input.customerId,
          receivables: input.receivables,
          expected_total: input.expectedTotal,
          execution_mode: "ONLINE",
          payment_method_code: input.paymentMethodCode,
          receiving_destination_id: input.receivingDestinationId,
          tendered_amount: input.tenderedAmount,
          local_change_amount: input.localChangeAmount,
          verification_source: input.verificationSource,
          verification_note: input.verificationNote ?? null,
          request_key: input.requestKey,
          client_context: input.clientContext ?? {},
        }),
        credentials: "omit",
        cache: "no-store",
        signal: controller.signal,
      });

      const payload = (await response.json().catch(() => null)) as GatewayEnvelope<ReceivableSettlementAllResult> | null;
      if (!response.ok || !payload?.ok || !payload.data) {
        return {
          ok: false,
          data: null,
          error: payload?.error ?? `GATEWAY_HTTP_${response.status}`,
          reasonCode: payload?.reason_code ?? null,
        };
      }
      return { ok: true, data: payload.data, error: null, reasonCode: null };
    } catch (error) {
      return {
        ok: false,
        data: null,
        error: error instanceof DOMException && error.name === "AbortError" ? "GATEWAY_TIMEOUT" : "GATEWAY_UNREACHABLE",
        reasonCode: null,
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}